/**
 * NOS-116: Import review panel — lists sections from import/AI generation for review.
 * NOS-178: also shows live progress for a running import job (resumable display).
 * NOS-179: lists images that couldn't be rehosted (originals kept, flagged here).
 * NOS-190: collapsible import report (skipped/failed pages, discovery sources).
 * NOS-192: preview gate — approve/stop actions while the job is paused at review.
 * NOS-183: per-page "Compare" opens the side-by-side review (source screenshot
 * vs imported render) when the job captured a screenshot for that page.
 */
import { useCallback, useEffect, useRef, useState } from "react"
import { Check, Columns2, Copy, Globe, ImageOff, Loader2, Lock, RefreshCw, SkipForward, X } from "lucide-react"
import { useCustomizationStore } from "@/stores/customizationStore"
import type { SectionWithPage } from "@/hooks/useImportReviewSections"
import {
  fetchImportJob,
  imageRetryStateFromJob,
  importReportIsClean,
  protectedRecoveryFromJob,
  recoverProtectedPages,
  retryFailedImages,
  retryableImageFailureCount,
  skipReasonLabel,
  type ImportImageRetryState,
  type ImportJobRow,
  type ImportImageFailure,
  type ImportContentSummary,
  type ImportOverflowSummary,
  type ImportProtectedPage,
  type ImportProtectedRecoveryState,
  type ImportRemainingSummary,
  type ImportSkippedPage,
  type ImportJobReport,
  type ImportBrandProposal,
  type ImportDecision,
  type RecoverPageStatus,
} from "@/hooks/useImportJob"
import { StopImportConfirmDialog } from "@/components/website-builder/StopImportConfirmDialog"
import { PANEL_CHROME } from "@/components/website-builder/ui/FloatingPanel"
import { ImportDecisionsBlock } from "./ImportDecisionsBlock"
import { DetectedFormsBlock } from "./DetectedFormsBlock"
import { useOrgStore } from "@/stores/orgStore"
import { getPublicSiteOrigin } from "@/lib/publicSiteUrl"

const IMPORT_REVIEW_STORAGE_KEY = "nos-import-review-dismissed"
const MAX_IMAGE_FAILURES_SHOWN = 6
const MAX_REPORT_ROWS_SHOWN = 5

export interface ImportReviewPanelProps {
  sections: SectionWithPage[]
  isLoading: boolean
  onRefresh: () => void
  onClose: () => void
  /** When true, shift panel left to avoid overlap with section context panel */
  shiftLeft?: boolean
  /** NOS-178: live import job — renders a progress row while crawl/mapping runs */
  activeJob?: ImportJobRow | null
  /** NOS-179: images that couldn't be downloaded — original URLs kept in content */
  imageFailures?: ImportImageFailure[]
  /** NOS-198: pages the mapper deliberately skipped (e.g. the blog index) */
  skippedPages?: ImportSkippedPage[]
  /** NOS-186: imported pages vs blog posts, from the job result */
  contentSummary?: ImportContentSummary | null
  /** NOS-187: crawl overflow by content type (falls back to the flat count) */
  overflowSummary?: ImportOverflowSummary | null
  /**
   * NOS-189: which scope the import ran with. `false` = content only (source
   * design was not carried over); `true`/undefined = content + design.
   */
  importDesign?: boolean | null
  /** NOS-192: job paused at the preview gate — renders approve/stop actions */
  reviewJob?: ImportJobRow | null
  /** NOS-192: what stage 2 will import ("N more pages, M more posts") */
  remainingSummary?: ImportRemainingSummary | null
  /** NOS-192: "Import everything" — resume stage 2 */
  onContinueImport?: () => Promise<void> | void
  /** NOS-192: "Stop here" — finalize with only the sample */
  onStopImport?: () => Promise<void> | void
  /** NOS-190: persisted import report from the job result */
  report?: ImportJobReport | null
  /** NOS-190: shows the page-cap override hint on capped imports */
  isPlatformAdmin?: boolean
  /** NOS-180: proposed brand from the vision pass — Apply / Keep current */
  brandProposal?: ImportBrandProposal | null
  /** NOS-214: guided-review decisions from the job report */
  decisions?: ImportDecision[]
  /** NOS-214: job id keying the persisted choice state */
  decisionsJobId?: string | null
  /** NOS-214: org whose content the decision choices write to */
  orgId?: string | null
  /** NOS-214: imported post slugs (targets for the posts decision) */
  importedPostSlugs?: string[]
  /** NOS-235: job whose failed images the retry button re-attempts */
  retryJobId?: string | null
  /** NOS-235: called after a retry pass finishes (refresh job + sections) */
  onImageRetryComplete?: () => void
  /** NOS-183: slug → hosted source-screenshot URL (empty = no compare buttons) */
  screenshots?: Record<string, string>
  /** NOS-183: opens the side-by-side compare view for a page */
  onOpenCompare?: (slug: string) => void
  /** NOS-253: pages imported as protected draft stubs (recovery card targets) */
  protectedPages?: ImportProtectedPage[]
  /** NOS-253: live recovery-pass progress from the job state */
  protectedRecovery?: ImportProtectedRecoveryState | null
  /** NOS-253: called after a recovery pass settles (refresh job + sections) */
  onProtectedRecovered?: () => void
}

/** NOS-189: review copy for the scope the import ran with. */
function importModeLabel(importDesign: boolean | null | undefined): string {
  return importDesign === false
    ? "Imported content only — your site uses your current NOS design."
    : "Imported content and design from your source site."
}

/** NOS-186: "3 pages, 12 blog posts imported" (posts omitted when none). */
function contentSummaryLabel(summary: ImportContentSummary): string {
  const pages = `${summary.pages} page${summary.pages !== 1 ? "s" : ""}`
  if (summary.posts === 0) return `${pages} imported`
  return `${pages}, ${summary.posts} blog post${summary.posts !== 1 ? "s" : ""} imported`
}

/** NOS-187: overflow copy — per-type breakdown when available. */
function overflowLabel(
  job: ImportJobRow | null,
  overflowSummary: ImportOverflowSummary | null | undefined
): string | null {
  if (overflowSummary && (overflowSummary.articles > 0 || overflowSummary.pages > 0)) {
    const parts: string[] = []
    if (overflowSummary.pages > 0) {
      parts.push(`${overflowSummary.pages} page${overflowSummary.pages !== 1 ? "s" : ""}`)
    }
    if (overflowSummary.articles > 0) {
      parts.push(
        `${overflowSummary.articles} blog post${overflowSummary.articles !== 1 ? "s" : ""}`
      )
    }
    return `${parts.join(" and ")} beyond the import limit were skipped.`
  }
  if (job && job.pages_overflow > 0) {
    return `${job.pages_overflow} page${job.pages_overflow !== 1 ? "s" : ""} beyond the import limit were skipped.`
  }
  return null
}

/** Compact display form of a failed image URL (host + filename). */
function shortImageUrl(url: string): string {
  try {
    const u = new URL(url)
    const file = u.pathname.split("/").filter(Boolean).pop() ?? u.pathname
    return `${u.hostname}/…/${file}`.slice(0, 52)
  } catch {
    return url.slice(0, 52)
  }
}

function ImageFailuresBlock({
  failures,
  jobId,
  onHealed,
}: {
  failures: ImportImageFailure[]
  /** NOS-235: enables the retry button when the job id is known */
  jobId?: string | null
  onHealed?: () => void
}) {
  const [retrying, setRetrying] = useState(false)
  const [progress, setProgress] = useState<ImportImageRetryState | null>(null)
  const [resultLabel, setResultLabel] = useState<string | null>(null)

  const retryableCount = retryableImageFailureCount(failures)

  const handleRetry = useCallback(async () => {
    if (!jobId || retrying) return
    setRetrying(true)
    setResultLabel(null)
    setProgress(null)
    // NOS-235: the function reports per-chunk progress via state.image_retry —
    // poll the job row while the pass runs so the button shows "Retrying N/M".
    const poll = setInterval(() => {
      void fetchImportJob(jobId).then((row) => {
        const p = imageRetryStateFromJob(row)
        if (p?.status === "running") setProgress(p)
      })
    }, 2000)
    const res = await retryFailedImages(jobId)
    clearInterval(poll)
    setProgress(null)
    setRetrying(false)
    if (!res.ok) {
      setResultLabel(res.error ?? "Retry failed. Please try again.")
    } else if ((res.recovered ?? 0) > 0) {
      const attempted = res.attempted ?? 0
      setResultLabel(
        `Recovered ${res.recovered} of ${attempted} image${attempted !== 1 ? "s" : ""}.`
      )
    } else {
      setResultLabel("No images could be recovered — the sources may no longer be available.")
    }
    onHealed?.()
  }, [jobId, retrying, onHealed])

  if (failures.length === 0) {
    // NOS-235: a fully-successful retry empties the list on refresh — show
    // the outcome once instead of vanishing without feedback.
    if (!resultLabel) return null
    return <div style={IMAGE_FAILURES_STYLE}>{resultLabel}</div>
  }
  return (
    <div style={IMAGE_FAILURES_STYLE}>
      <div style={IMAGE_FAILURES_TITLE_STYLE}>
        <ImageOff size={12} style={{ flexShrink: 0 }} />
        {failures.length} image{failures.length !== 1 ? "s" : ""} couldn&apos;t be imported
      </div>
      <div style={{ opacity: 0.75, marginBottom: 6 }}>
        These still link to the original site and may break if it goes offline.
      </div>
      {failures.slice(0, MAX_IMAGE_FAILURES_SHOWN).map((f) => (
        <div key={`${f.slug ?? ""}|${f.url}`} style={IMAGE_FAILURE_ROW_STYLE} title={`${f.url} — ${f.reason}`}>
          {f.slug ? `${f.slug}: ` : ""}
          {shortImageUrl(f.url)}
        </div>
      ))}
      {failures.length > MAX_IMAGE_FAILURES_SHOWN && (
        <div style={{ opacity: 0.6 }}>
          +{failures.length - MAX_IMAGE_FAILURES_SHOWN} more
        </div>
      )}
      {/* NOS-235: retry only the recorded failures — no full re-import needed */}
      {jobId && retryableCount > 0 && (
        <button
          type="button"
          onClick={handleRetry}
          disabled={retrying}
          style={{ ...RETRY_IMAGES_BTN_STYLE, opacity: retrying ? 0.7 : 1 }}
        >
          {retrying ? (
            <Loader2 size={11} style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />
          ) : (
            <RefreshCw size={11} style={{ flexShrink: 0 }} />
          )}
          {retrying
            ? progress
              ? `Retrying ${progress.attempted}/${progress.total}…`
              : "Retrying…"
            : `Retry failed images (${retryableCount})`}
        </button>
      )}
      {resultLabel && <div style={{ marginTop: 5, opacity: 0.85 }}>{resultLabel}</div>}
    </div>
  )
}

/**
 * NOS-198: pages the mapper deliberately skipped. Rendered as explicit skip
 * rows ("Skipped — served by your blog") so a blog-index route never reads as a
 * failed "0 sections imported."
 */
function SkippedPagesBlock({ skipped }: { skipped: ImportSkippedPage[] }) {
  if (skipped.length === 0) return null
  return (
    <div style={SKIPPED_BLOCK_STYLE}>
      {skipped.map((p) => (
        <div key={`${p.slug}|${p.reason}`} style={SKIPPED_ROW_STYLE}>
          <SkipForward size={11} style={{ flexShrink: 0, opacity: 0.7 }} />
          <span style={SKIPPED_SLUG_STYLE}>{p.slug === "home" ? "Home" : p.slug}</span>
          <span style={{ opacity: 0.7 }}>{skipReasonLabel(p.reason)}</span>
        </div>
      ))}
    </div>
  )
}

/** NOS-253: user-facing copy per unlock outcome. */
export function unlockStatusLabel(status: RecoverPageStatus): string {
  switch (status) {
    case "unlocked":
      return "Unlocked — importing content…"
    case "wrong_password":
      return "Password didn't match — try again"
    case "attempts_exhausted":
      return "Too many attempts — page stays a draft"
    case "not_attempted":
      return "Not attempted yet — run unlock again"
    case "no_password":
      return "No password entered"
    default:
      return "Couldn't reach this page — try again"
  }
}

/**
 * NOS-253: password-gated pages card. Lists pages imported as protected draft
 * stubs, takes one shared password (with per-page overrides), and drives the
 * recover-protected-pages flow. "Skip" just dismisses the card — the NOS-227
 * draft-stub behavior stays exactly as it is. The password lives in component
 * state and the one request body only; it is cleared after every attempt.
 */
function ProtectedPagesBlock({
  pages,
  jobId,
  recovery,
  onRecovered,
}: {
  pages: ImportProtectedPage[]
  jobId?: string | null
  /** Live pass state from the job row (survives panel remounts). */
  recovery: ImportProtectedRecoveryState | null
  onRecovered?: () => void
}) {
  const [password, setPassword] = useState("")
  const [overrides, setOverrides] = useState<Record<string, string>>({})
  const [showOverrides, setShowOverrides] = useState(false)
  const [running, setRunning] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [pageStatus, setPageStatus] = useState<Record<string, RecoverPageStatus>>({})
  const [resultLabel, setResultLabel] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // A mapping pass started in another session/panel instance still shows live.
  const mappingLive = recovery?.status === "mapping" || recovery?.status === "running"

  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current)
    },
    []
  )

  const handleUnlock = useCallback(async () => {
    if (!jobId || running) return
    const pagePasswords: Record<string, string> = {}
    for (const [slug, pw] of Object.entries(overrides)) {
      if (pw.trim() !== "") pagePasswords[slug] = pw
    }
    if (password === "" && Object.keys(pagePasswords).length === 0) return
    setRunning(true)
    setResultLabel(null)
    const res = await recoverProtectedPages({
      jobId,
      ...(password !== "" ? { password } : {}),
      pagePasswords,
    })
    // The password has been submitted — drop it from component state now.
    setPassword("")
    setOverrides({})
    if (!res.ok) {
      setRunning(false)
      setResultLabel(res.error ?? "Recovery failed. Please try again.")
      return
    }
    const statuses: Record<string, RecoverPageStatus> = {}
    for (const p of res.pages ?? []) statuses[p.slug] = p.status
    setPageStatus(statuses)
    if (!res.mapping) {
      setRunning(false)
      const wrong = (res.pages ?? []).filter((p) => p.status === "wrong_password").length
      setResultLabel(
        wrong > 0 ? "The password didn't unlock any pages. Check it and try again." : null
      )
      return
    }
    // Unlocked pages are being mapped — poll until the pass settles, then
    // refresh so the recovered pages drop off this list.
    pollRef.current = setInterval(() => {
      void fetchImportJob(jobId).then((row) => {
        const rec = protectedRecoveryFromJob(row)
        if (rec && rec.status !== "done") return
        if (pollRef.current) clearInterval(pollRef.current)
        pollRef.current = null
        setRunning(false)
        const recovered = rec?.recovered.length ?? 0
        setResultLabel(
          recovered > 0
            ? `Imported ${recovered} protected page${recovered !== 1 ? "s" : ""}.`
            : "No pages could be imported — check the password and try again."
        )
        onRecovered?.()
      })
    }, 2500)
  }, [jobId, running, password, overrides, onRecovered])

  if (dismissed) return null
  if (pages.length === 0) {
    // A fully-successful pass empties the list on refresh — show the outcome
    // once instead of vanishing without feedback (NOS-235 convention).
    if (!resultLabel) return null
    return <div style={PROTECTED_BLOCK_STYLE}>{resultLabel}</div>
  }

  const busy = running || mappingLive
  return (
    <div style={PROTECTED_BLOCK_STYLE}>
      <div style={PROTECTED_TITLE_STYLE}>
        <Lock size={12} style={{ flexShrink: 0 }} />
        {pages.length} password-protected page{pages.length !== 1 ? "s" : ""}
      </div>
      <div style={{ opacity: 0.75, marginBottom: 6 }}>
        These imported as unpublished drafts. Enter the page password from your old site to
        import the real content.
      </div>
      {pages.map((p) => (
        <div key={p.slug} style={PROTECTED_ROW_STYLE} title={p.url}>
          <span style={{ fontWeight: 600, color: "rgba(255,255,255,0.75)" }}>
            {p.slug === "home" ? "Home" : p.slug}
          </span>
          {pageStatus[p.slug] && (
            <span
              style={{
                marginLeft: 6,
                color: pageStatus[p.slug] === "unlocked" ? "#4ade80" : "#f87171",
              }}
            >
              {unlockStatusLabel(pageStatus[p.slug])}
            </span>
          )}
          {showOverrides && (
            <input
              type="password"
              autoComplete="off"
              placeholder="Different password"
              value={overrides[p.slug] ?? ""}
              onChange={(e) =>
                setOverrides((o) => ({ ...o, [p.slug]: e.target.value }))
              }
              disabled={busy}
              style={PROTECTED_INPUT_STYLE}
              aria-label={`Password for ${p.slug}`}
            />
          )}
        </div>
      ))}
      <input
        type="password"
        autoComplete="off"
        placeholder="Page password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={busy}
        style={{ ...PROTECTED_INPUT_STYLE, width: "100%", marginTop: 6 }}
        aria-label="Shared page password"
      />
      {pages.length > 1 && (
        <button
          type="button"
          onClick={() => setShowOverrides((v) => !v)}
          style={PROTECTED_TOGGLE_STYLE}
        >
          {showOverrides ? "Hide per-page passwords" : "Different password per page?"}
        </button>
      )}
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <button
          type="button"
          onClick={() => void handleUnlock()}
          disabled={busy || (password === "" && Object.values(overrides).every((v) => v.trim() === ""))}
          style={{ ...PROTECTED_UNLOCK_BTN_STYLE, opacity: busy ? 0.7 : 1 }}
        >
          {busy ? (
            <Loader2 size={11} style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />
          ) : (
            <Lock size={11} style={{ flexShrink: 0 }} />
          )}
          {busy ? "Unlocking…" : "Unlock pages"}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          disabled={busy}
          style={PROTECTED_SKIP_BTN_STYLE}
        >
          Skip — keep as drafts
        </button>
      </div>
      {resultLabel && <div style={{ marginTop: 5, opacity: 0.85 }}>{resultLabel}</div>}
    </div>
  )
}

/** NOS-192: "Your site has 42 more pages and 340 more posts ready to import." */
export function remainingLabel(
  remaining: ImportRemainingSummary | null | undefined
): string | null {
  if (!remaining || (remaining.pages === 0 && remaining.posts === 0)) return null
  const parts: string[] = []
  if (remaining.pages > 0) {
    parts.push(`${remaining.pages} more page${remaining.pages !== 1 ? "s" : ""}`)
  }
  if (remaining.posts > 0) {
    parts.push(`${remaining.posts} more post${remaining.posts !== 1 ? "s" : ""}`)
  }
  return `Your site has ${parts.join(" and ")} ready to import.`
}

/** NOS-192: approve/stop actions while the job is paused at the preview gate. */
function ReviewGateBlock({
  remaining,
  onContinue,
  onStop,
}: {
  remaining: ImportRemainingSummary | null | undefined
  onContinue?: () => Promise<void> | void
  onStop?: () => Promise<void> | void
}) {
  const [acting, setActing] = useState<"continue" | "stop" | null>(null)
  const run = useCallback(
    async (which: "continue" | "stop", fn?: () => Promise<void> | void) => {
      if (!fn) return
      setActing(which)
      try {
        await fn()
      } finally {
        setActing(null)
      }
    },
    []
  )
  const moreLabel = remainingLabel(remaining)
  return (
    <div style={REVIEW_GATE_STYLE}>
      <div style={{ fontWeight: 600, color: "rgba(255,255,255,0.9)", marginBottom: 4 }}>
        Preview imported — approve to continue
      </div>
      <div style={{ opacity: 0.8, marginBottom: 8 }}>
        {moreLabel ?? "Check the preview, then import the rest of your site."}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          onClick={() => run("continue", onContinue)}
          disabled={acting != null}
          style={{ ...REVIEW_PRIMARY_BTN_STYLE, opacity: acting ? 0.6 : 1 }}
        >
          {acting === "continue" ? "Starting…" : "Import everything"}
        </button>
        {/* NOS-198: "Stop here" finalizes sample-only — confirm the discard first */}
        <StopImportConfirmDialog
          remaining={remaining}
          disabled={acting != null}
          onConfirm={() => run("stop", onStop)}
          trigger={
            <button
              type="button"
              disabled={acting != null}
              style={{ ...REVIEW_SECONDARY_BTN_STYLE, opacity: acting ? 0.6 : 1 }}
            >
              {acting === "stop" ? "Stopping…" : "Stop here"}
            </button>
          }
        />
      </div>
    </div>
  )
}

/**
 * NOS-180: proposed brand card — palette swatches + type sample with explicit
 * Apply / Keep current. Keep current is the default (nothing happens unless
 * the user opts in); Apply writes through the standard design-token path.
 */
function BrandProposalCard({ proposal }: { proposal: ImportBrandProposal }) {
  const updateDesignTokens = useCustomizationStore((s) => s.updateDesignTokens)
  // Already-applied detection survives reloads: the card's own state is
  // transient, but the stored tokens aren't — without this, a successful
  // Apply reads as "reverted" after refresh (the button just comes back).
  const storedPrimary = useCustomizationStore(
    (s) =>
      (s.customizations?.design_tokens as { colors?: { primary?: string } } | null | undefined)
        ?.colors?.primary
  )
  const alreadyApplied =
    typeof storedPrimary === "string" &&
    storedPrimary.toLowerCase() === proposal.tokens.colors.primary.toLowerCase()
  const [state, setState] = useState<"idle" | "applying" | "applied" | "error" | "dismissed">(
    alreadyApplied ? "applied" : "idle"
  )
  if (state === "dismissed") return null

  const { colors, typography } = proposal.tokens
  const swatches = [
    { label: "Primary", value: colors.primary },
    ...(colors.secondary ? [{ label: "Secondary", value: colors.secondary }] : []),
    ...(colors.accent ? [{ label: "Accent", value: colors.accent }] : []),
  ]

  const apply = async () => {
    setState("applying")
    try {
      await updateDesignTokens(proposal.tokens)
      setState("applied")
    } catch (err) {
      // Surface the failure — a silent reset reads as success-then-revert.
      console.error("[BrandProposalCard] apply failed:", err)
      setState("error")
    }
  }

  return (
    <div style={BRAND_CARD_STYLE}>
      <div style={{ fontWeight: 600, fontSize: 11, color: "#fff", marginBottom: 6 }}>
        Proposed brand from your old site
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        {swatches.map((s) => (
          <div key={s.label} style={{ textAlign: "center" }}>
            <div
              title={`${s.label}: ${s.value}`}
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                background: s.value,
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            />
            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.45)", marginTop: 3 }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>
      {typography?.headingFamily && (
        <div
          style={{
            fontFamily: typography.headingFamily,
            fontSize: 15,
            color: "rgba(255,255,255,0.85)",
            marginBottom: 8,
          }}
        >
          Heading in {typography.headingFamily.split(",")[0]}
        </div>
      )}
      {proposal.imagery_style && proposal.imagery_style.length > 0 && (
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>
          Imagery: {proposal.imagery_style.join(" · ")}
        </div>
      )}
      {state === "applied" ? (
        <div style={{ fontSize: 11, color: "#4ade80" }}>
          Brand applied — headings stay dark; your brand color lands on buttons and accents.
        </div>
      ) : (
        <>
          {state === "error" && (
            <div style={{ fontSize: 11, color: "#f87171", marginBottom: 6 }}>
              Couldn&apos;t save the brand — try again. If it keeps failing, refresh the builder
              first.
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() => void apply()}
            disabled={state === "applying"}
            style={{
              padding: "6px 12px",
              background: colors.primary,
              border: "none",
              borderRadius: 6,
              color: "#fff",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              opacity: state === "applying" ? 0.6 : 1,
            }}
          >
            {state === "applying" ? "Applying…" : "Apply this brand"}
          </button>
          <button
            type="button"
            onClick={() => setState("dismissed")}
            style={{
              padding: "6px 12px",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 6,
              color: "rgba(255,255,255,0.7)",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            Keep current
          </button>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * NOS-190: import report. Clean imports get one quiet line; anything skipped
 * or failed gets a prominent summary with raw detail behind a toggle — never
 * a raw JSON dump.
 */
function ImportReportBlock({
  report,
  isPlatformAdmin = false,
}: {
  report: ImportJobReport
  isPlatformAdmin?: boolean
}) {
  const [showDetails, setShowDetails] = useState(false)

  if (importReportIsClean(report)) {
    const total = report.imported.pages + report.imported.posts
    if (total === 0) return null
    return (
      <div style={REPORT_CLEAN_STYLE}>
        All {total} page{total !== 1 ? "s" : ""} imported successfully.
        {report.redirects_created > 0 && (
          <span style={{ display: "block" }}>
            {report.redirects_created} redirect{report.redirects_created !== 1 ? "s" : ""} keep
            your old links (and Google rankings) working.
          </span>
        )}
      </div>
    )
  }

  const { skipped_over_cap: skipped } = report
  const skippedParts: string[] = []
  if (skipped.pages > 0) skippedParts.push(`${skipped.pages} page${skipped.pages !== 1 ? "s" : ""}`)
  if (skipped.articles > 0) {
    skippedParts.push(`${skipped.articles} post${skipped.articles !== 1 ? "s" : ""}`)
  }

  return (
    <div style={REPORT_STYLE}>
      <div style={REPORT_TITLE_STYLE}>Import report</div>
      {report.homepage_missing && (
        <div style={REPORT_HOMEPAGE_MISSING_STYLE}>
          Your homepage could not be imported — your site currently has no home page. Add one
          in the builder, or try importing again.
        </div>
      )}
      {skippedParts.length > 0 && (
        <div style={REPORT_ROW_STYLE}>
          {skippedParts.join(" and ")} beyond the import limit ({report.page_cap} pages) were
          skipped.
          {isPlatformAdmin && skipped.pages > 0 && (
            <span style={{ display: "block", opacity: 0.75 }}>
              As a platform admin you can raise the page cap when starting an import.
            </span>
          )}
        </div>
      )}
      {report.failed_crawls.length > 0 && (
        <div style={REPORT_ROW_STYLE}>
          {report.failed_crawls.length} page{report.failed_crawls.length !== 1 ? "s" : ""} couldn&apos;t
          be read from your old site.
        </div>
      )}
      {report.failed_maps.length > 0 && (
        <div style={REPORT_ROW_STYLE}>
          {report.failed_maps.length} page{report.failed_maps.length !== 1 ? "s" : ""} couldn&apos;t be
          converted into your new site.
        </div>
      )}
      {report.image_failure_count > 0 && (
        <div style={REPORT_ROW_STYLE}>
          {report.image_failure_count} image{report.image_failure_count !== 1 ? "s" : ""} couldn&apos;t
          be imported.
        </div>
      )}
      {report.footer_background_missing && (
        <div style={REPORT_ROW_STYLE}>
          We couldn&apos;t detect your old site&apos;s footer background color, so your footer uses
          the default dark style. You can change it in the footer panel.
        </div>
      )}
      {report.empty_pages.length > 0 && (
        <div style={REPORT_ROW_STYLE}>
          {report.empty_pages.length} page{report.empty_pages.length !== 1 ? "s" : ""} in your
          navigation imported empty ({report.empty_pages.slice(0, 3).join(", ")}
          {report.empty_pages.length > 3 ? ", and more" : ""}). The source page could not be
          read - add content in the builder or re-import.
        </div>
      )}
      {report.protected_drafts.length > 0 && (
        <div style={REPORT_ROW_STYLE}>
          {report.protected_drafts.length} page{report.protected_drafts.length !== 1 ? "s were" : " was"} password-protected
          on your old site and imported as unpublished draft{report.protected_drafts.length !== 1 ? "s" : ""} (
          {report.protected_drafts.slice(0, 3).join(", ")}
          {report.protected_drafts.length > 3 ? ", and more" : ""}). Add their content in the
          builder, then publish when ready.
        </div>
      )}
      {report.unsupported_videos.length > 0 && (
        <div style={REPORT_ROW_STYLE}>
          {report.unsupported_videos.length} video{report.unsupported_videos.length !== 1 ? "s" : ""} on
          your old site use{report.unsupported_videos.length === 1 ? "s" : ""} a player NOS
          can&apos;t embed yet, so {report.unsupported_videos.length === 1 ? "it was" : "they were"} not
          imported. You can re-add {report.unsupported_videos.length === 1 ? "it" : "them"} with a
          YouTube or Vimeo link.
        </div>
      )}
      {report.redirects_created > 0 && (
        <div style={REPORT_ROW_STYLE}>
          {report.redirects_created} redirect{report.redirects_created !== 1 ? "s" : ""} keep your
          old links (and Google rankings) working.
        </div>
      )}
      <button
        type="button"
        onClick={() => setShowDetails((v) => !v)}
        style={REPORT_TOGGLE_STYLE}
      >
        {showDetails ? "Hide details" : "Show details"}
      </button>
      {showDetails && (
        <div style={REPORT_DETAILS_STYLE}>
          {skipped.example_urls.length > 0 && (
            <>
              <div style={REPORT_DETAIL_HEADING_STYLE}>Skipped over the limit (examples)</div>
              {skipped.example_urls.slice(0, MAX_REPORT_ROWS_SHOWN).map((u) => (
                <div key={u} style={REPORT_DETAIL_ROW_STYLE} title={u}>
                  {shortImageUrl(u)}
                </div>
              ))}
            </>
          )}
          {report.failed_crawls.length > 0 && (
            <>
              <div style={REPORT_DETAIL_HEADING_STYLE}>Couldn&apos;t be read</div>
              {report.failed_crawls.slice(0, MAX_REPORT_ROWS_SHOWN).map((u) => (
                <div key={u} style={REPORT_DETAIL_ROW_STYLE} title={u}>
                  {shortImageUrl(u)}
                </div>
              ))}
              {report.failed_crawls.length > MAX_REPORT_ROWS_SHOWN && (
                <div style={{ opacity: 0.6 }}>
                  +{report.failed_crawls.length - MAX_REPORT_ROWS_SHOWN} more
                </div>
              )}
            </>
          )}
          {report.failed_maps.length > 0 && (
            <>
              <div style={REPORT_DETAIL_HEADING_STYLE}>Couldn&apos;t be converted</div>
              {report.failed_maps.slice(0, MAX_REPORT_ROWS_SHOWN).map((f) => (
                <div
                  key={`${f.slug}|${f.reason}`}
                  style={REPORT_DETAIL_ROW_STYLE}
                  title={`${f.slug}: ${f.reason}`}
                >
                  {f.slug}: {f.reason}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function jobProgressLabel(job: ImportJobRow): string {
  const found = job.pages_found
  const processed = job.pages_processed
  if (job.status === "crawling") {
    return found > 0
      ? `Crawling ${processed} of ${found} pages…`
      : job.phase ?? "Crawling your site…"
  }
  if (job.status === "mapping") {
    return found > 0
      ? `Importing ${Math.min(processed + 1, found)} of ${found} pages…`
      : job.phase ?? "Importing content…"
  }
  return job.phase ?? "Importing…"
}


export function ImportReviewPanel({
  sections,
  isLoading,
  onRefresh,
  onClose,
  shiftLeft = false,
  activeJob = null,
  imageFailures = [],
  skippedPages = [],
  contentSummary = null,
  overflowSummary = null,
  importDesign = null,
  reviewJob = null,
  remainingSummary = null,
  onContinueImport,
  onStopImport,
  report = null,
  brandProposal = null,
  isPlatformAdmin = false,
  decisions = [],
  decisionsJobId = null,
  orgId = null,
  importedPostSlugs = [],
  retryJobId = null,
  onImageRetryComplete,
  screenshots = {},
  onOpenCompare,
  protectedPages = [],
  protectedRecovery = null,
  onProtectedRecovered,
}: ImportReviewPanelProps) {
  const markSectionReviewed = useCustomizationStore((s) => s.markSectionReviewed)
  const setCurrentPage = useCustomizationStore((s) => s.setCurrentPage)
  const pages = useCustomizationStore((s) => s.pages)

  const handleMarkPageReviewed = useCallback(
    async (items: SectionWithPage[]) => {
      for (const { section } of items) {
        await markSectionReviewed(section.id)
      }
      onRefresh()
    },
    [markSectionReviewed, onRefresh]
  )

  const handleMarkAllReviewed = useCallback(async () => {
    for (const { section } of sections) {
      await markSectionReviewed(section.id)
    }
    onRefresh()
    const orgId = window.location.pathname.includes("/builder") ? "session" : ""
    try {
      localStorage.setItem(`${IMPORT_REVIEW_STORAGE_KEY}-${orgId}`, Date.now().toString())
    } catch {
      /* ignore */
    }
  }, [sections, markSectionReviewed, onRefresh])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleGoToSection = useCallback(
    (pageSlug: string) => {
      const page = pages.find((p) => p.slug === pageSlug)
      if (page) setCurrentPage(page)
    },
    [pages, setCurrentPage]
  )

  const panelStyle = {
    ...PANEL_STYLE,
    right: shiftLeft ? 320 : 16,
  }

  if (isLoading) {
    return (
      <div style={panelStyle}>
        <div style={HEADER_STYLE}>
          <span style={TITLE_STYLE}>Import review</span>
          <button type="button" onClick={handleClose} style={CLOSE_BTN_STYLE} aria-label="Close">
            <X size={14} />
          </button>
        </div>
        <div style={{ padding: 24, color: "rgba(255,255,255,0.5)", fontSize: 12 }}>
          Loading…
        </div>
      </div>
    )
  }

  // Live job with nothing to review yet — progress-only panel
  if (sections.length === 0 && activeJob) {
    return (
      <div style={panelStyle}>
        <div style={HEADER_STYLE}>
          <span style={TITLE_STYLE}>Importing your site</span>
          <button type="button" onClick={handleClose} style={CLOSE_BTN_STYLE} aria-label="Close">
            <X size={14} />
          </button>
        </div>
        <div style={JOB_PROGRESS_STYLE}>
          <Loader2 size={12} style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />
          <span>{jobProgressLabel(activeJob)}</span>
        </div>
        {overflowLabel(activeJob, overflowSummary) && (
          <div style={{ ...PROGRESS_STYLE, paddingTop: 0 }}>
            {overflowLabel(activeJob, overflowSummary)}
          </div>
        )}
      </div>
    )
  }

  // NOS-192: paused at the preview gate with no reviewable sections loaded yet
  if (sections.length === 0 && reviewJob) {
    return (
      <div style={panelStyle}>
        <div style={HEADER_STYLE}>
          <span style={TITLE_STYLE}>Import review</span>
          <button type="button" onClick={handleClose} style={CLOSE_BTN_STYLE} aria-label="Close">
            <X size={14} />
          </button>
        </div>
        <ReviewGateBlock
          remaining={remainingSummary}
          onContinue={onContinueImport}
          onStop={onStopImport}
        />
        {/* NOS-253: unlock password-gated pages right at the review pause */}
        <ProtectedPagesBlock
          pages={protectedPages}
          jobId={retryJobId}
          recovery={protectedRecovery}
          onRecovered={onProtectedRecovered}
        />
        <SkippedPagesBlock skipped={skippedPages} />
        {brandProposal && <BrandProposalCard proposal={brandProposal} />}
        {/* NOS-214: guided decisions — features, not failures */}
        <ImportDecisionsBlock
          decisions={decisions}
          jobId={decisionsJobId}
          orgId={orgId}
          importedPostSlugs={importedPostSlugs}
          onApplied={onRefresh}
        />
        {report && <ImportReportBlock report={report} isPlatformAdmin={isPlatformAdmin} />}
        <ImageFailuresBlock
          failures={imageFailures}
          jobId={retryJobId}
          onHealed={onImageRetryComplete}
        />
      </div>
    )
  }

  if (sections.length === 0) return null

  const byPage = sections.reduce<Record<string, SectionWithPage[]>>((acc, item) => {
    const key = `${item.pageSlug}|${item.pageTitle ?? item.pageSlug}`
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})

  return (
    <div style={panelStyle}>
      <div style={HEADER_STYLE}>
        <span style={TITLE_STYLE}>Import review</span>
        <button type="button" onClick={handleClose} style={CLOSE_BTN_STYLE} aria-label="Close">
          <X size={14} />
        </button>
      </div>
      {activeJob && (
        <div style={JOB_PROGRESS_STYLE}>
          <Loader2 size={12} style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />
          <span>{jobProgressLabel(activeJob)}</span>
        </div>
      )}
      {/* NOS-192: preview gate actions lead — everything else is context */}
      {reviewJob && (
        <ReviewGateBlock
          remaining={remainingSummary}
          onContinue={onContinueImport}
          onStop={onStopImport}
        />
      )}
      <div style={PROGRESS_STYLE}>
        {sections.length} Section{sections.length !== 1 ? "s" : ""} Imported
        {contentSummary && (
          <span style={{ display: "block", marginTop: 2 }}>
            {contentSummaryLabel(contentSummary)}
            {contentSummary.posts > 0 && " — posts go live with your site (edit in the Blog tab)"}
          </span>
        )}
        {overflowLabel(activeJob, overflowSummary) && (
          <span style={{ display: "block", marginTop: 2, opacity: 0.8 }}>
            {overflowLabel(activeJob, overflowSummary)}
          </span>
        )}
        {/* NOS-189: which scope the import ran with */}
        <span style={{ display: "block", marginTop: 2, opacity: 0.8 }}>
          {importModeLabel(importDesign)}
        </span>
      </div>
      {/* NOS-253: unlock password-gated pages — shown wherever the latest job
          carries protected drafts (covers the post-done late-entry path too) */}
      <ProtectedPagesBlock
        pages={protectedPages}
        jobId={retryJobId}
        recovery={protectedRecovery}
        onRecovered={onProtectedRecovered}
      />
      {/* NOS-198: deliberately skipped pages (e.g. the blog index) */}
      <SkippedPagesBlock skipped={skippedPages} />
      {/* NOS-180: proposed brand — shown wherever the latest job carries one,
          not only at the preview-gate pause (a job that ran straight to done
          would otherwise never surface its proposal) */}
      {brandProposal && <BrandProposalCard proposal={brandProposal} />}
      {/* NOS-214: guided decisions — surfaced wherever the latest job carries
          them (mirrors the brand card: a job that ran straight to done must
          still get its guided review) */}
      <ImportDecisionsBlock
        decisions={decisions}
        jobId={decisionsJobId}
        orgId={orgId}
        importedPostSlugs={importedPostSlugs}
        onApplied={onRefresh}
      />
      {/* NOS-182: inert imported forms — convert to the wired NOS block or keep */}
      <DetectedFormsBlock sections={sections} onApplied={onRefresh} />
      {/* NOS-190: import report — quiet when clean, prominent when not */}
      {report && <ImportReportBlock report={report} isPlatformAdmin={isPlatformAdmin} />}
      {/* NOS-242: post-import SEO nudge — resubmit the sitemap in Search
          Console (shown once the job has produced its report, i.e. done) */}
      {report && !reviewJob && <SearchConsoleBlock />}
      <ImageFailuresBlock
        failures={imageFailures}
        jobId={retryJobId}
        onHealed={onImageRetryComplete}
      />
      <button
        type="button"
        onClick={handleMarkAllReviewed}
        style={MARK_ALL_BTN_STYLE}
      >
        <Check size={12} />
        Mark all as reviewed
      </button>
      <div style={LIST_STYLE}>
        {Object.entries(byPage).map(([key, items]) => {
          const [slug] = key.split("|")
          const pageTitle = items[0]?.pageTitle ?? slug
          const count = items.length
          // NOS-190: discovery source (sitemap / nav / link / homepage) on hover
          const source = report?.sources[slug]
          return (
            <div key={key} style={SECTION_ROW_STYLE}>
              <span
                style={SECTION_LABEL_STYLE}
                title={source ? `Found via ${source}` : undefined}
              >
                <span style={PAGE_LABEL_STYLE}>{pageTitle}</span>
                <span style={{ display: "block" }}>{count} Section{count !== 1 ? "s" : ""} Imported</span>
              </span>
              {/* NOS-183: side-by-side compare — only when a source screenshot
                  was captured for this page (missing = single-pane review) */}
              {onOpenCompare && screenshots[slug] && (
                <button
                  type="button"
                  onClick={() => onOpenCompare(slug)}
                  style={COMPARE_BTN_STYLE}
                  title="Compare with your old site"
                  aria-label={`Compare ${pageTitle} with your old site`}
                >
                  <Columns2 size={12} />
                </button>
              )}
              <button
                type="button"
                onClick={() => handleGoToSection(slug)}
                style={LINK_BTN_STYLE}
              >
                View
              </button>
              <button
                type="button"
                onClick={() => handleMarkPageReviewed(items)}
                style={CHECK_BTN_STYLE}
                aria-label={`Mark ${pageTitle} as reviewed`}
              >
                <Check size={12} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * NOS-242: post-import Search Console nudge — the imported site now serves
 * its own sitemap.xml (site root, custom domain aware), so resubmitting it
 * in Google Search Console gets the new URLs crawled sooner.
 */
export function SearchConsoleBlock() {
  const currentOrg = useOrgStore((s) => s.currentOrg)
  const [copied, setCopied] = useState(false)
  if (!currentOrg?.slug) return null
  const sitemapUrl = `${getPublicSiteOrigin(currentOrg)}/sitemap.xml`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(sitemapUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard unavailable (permissions/non-secure context) — ignore.
    }
  }

  return (
    <div style={SEARCH_CONSOLE_STYLE}>
      <div style={SEARCH_CONSOLE_TITLE_STYLE}>
        <Globe size={11} />
        Boost your search ranking
      </div>
      <div style={{ marginBottom: 6 }}>
        Your site now publishes a sitemap. Resubmit it in{" "}
        <a
          href="https://search.google.com/search-console"
          target="_blank"
          rel="noreferrer"
          style={{ color: "#60a5fa", textDecoration: "underline" }}
        >
          Google Search Console
        </a>{" "}
        so Google finds your new pages faster.
      </div>
      <button type="button" onClick={handleCopy} style={SEARCH_CONSOLE_COPY_STYLE} title={sitemapUrl}>
        {copied ? <Check size={10} /> : <Copy size={10} />}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {copied ? "Copied!" : sitemapUrl}
        </span>
      </button>
    </div>
  )
}

const PANEL_STYLE: React.CSSProperties = {
  ...PANEL_CHROME,
  top: 100,
  right: 16,
  width: 280,
  maxHeight: "calc(100vh - 116px)",
  zIndex: 100,
}

const HEADER_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 14px",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
}

const TITLE_STYLE: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "rgba(255,255,255,0.9)",
}

const CLOSE_BTN_STYLE: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "rgba(255,255,255,0.5)",
  cursor: "pointer",
  padding: 4,
}

const PROGRESS_STYLE: React.CSSProperties = {
  padding: "10px 14px",
  fontSize: 11,
  color: "rgba(255,255,255,0.6)",
}

const IMAGE_FAILURES_STYLE: React.CSSProperties = {
  margin: "0 14px 10px",
  padding: "8px 10px",
  background: "rgba(245,158,11,0.08)",
  border: "1px solid rgba(245,158,11,0.25)",
  borderRadius: 8,
  fontSize: 10,
  color: "rgba(255,255,255,0.75)",
}

const IMAGE_FAILURES_TITLE_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontWeight: 600,
  fontSize: 11,
  color: "#f59e0b",
  marginBottom: 4,
}

const SEARCH_CONSOLE_STYLE: React.CSSProperties = {
  margin: "0 14px 10px",
  padding: "8px 10px",
  background: "rgba(96,165,250,0.08)",
  border: "1px solid rgba(96,165,250,0.25)",
  borderRadius: 8,
  fontSize: 10,
  color: "rgba(255,255,255,0.75)",
}

const SEARCH_CONSOLE_TITLE_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontWeight: 600,
  fontSize: 11,
  color: "#60a5fa",
  marginBottom: 4,
}

const SEARCH_CONSOLE_COPY_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 5,
  width: "100%",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 6,
  color: "rgba(255,255,255,0.8)",
  cursor: "pointer",
  fontSize: 10,
  padding: "4px 8px",
  textAlign: "left",
}

const IMAGE_FAILURE_ROW_STYLE: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  padding: "1px 0",
}

// NOS-235: "Retry failed images" — retries only the recorded failures
const RETRY_IMAGES_BTN_STYLE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  marginTop: 6,
  padding: "4px 8px",
  background: "rgba(245,158,11,0.15)",
  border: "1px solid rgba(245,158,11,0.4)",
  borderRadius: 6,
  color: "#f59e0b",
  fontSize: 10,
  fontWeight: 600,
  cursor: "pointer",
}

// === NOS-253: password-gated page recovery ===

const PROTECTED_BLOCK_STYLE: React.CSSProperties = {
  margin: "0 14px 10px",
  padding: "8px 10px",
  background: "rgba(124,58,237,0.08)",
  border: "1px solid rgba(124,58,237,0.35)",
  borderRadius: 8,
  fontSize: 10,
  color: "rgba(255,255,255,0.75)",
}

const PROTECTED_TITLE_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontWeight: 600,
  fontSize: 11,
  color: "#a78bfa",
  marginBottom: 4,
}

const PROTECTED_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 4,
  padding: "2px 0",
}

const PROTECTED_INPUT_STYLE: React.CSSProperties = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 6,
  color: "rgba(255,255,255,0.9)",
  fontSize: 11,
  padding: "5px 8px",
  outline: "none",
}

const PROTECTED_TOGGLE_STYLE: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "rgba(255,255,255,0.6)",
  cursor: "pointer",
  fontSize: 10,
  textDecoration: "underline",
  padding: "4px 0 0",
}

const PROTECTED_UNLOCK_BTN_STYLE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "5px 10px",
  background: "rgba(124,58,237,0.45)",
  border: "1px solid rgba(124,58,237,0.7)",
  borderRadius: 6,
  color: "#fff",
  fontSize: 10,
  fontWeight: 600,
  cursor: "pointer",
}

const PROTECTED_SKIP_BTN_STYLE: React.CSSProperties = {
  padding: "5px 10px",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 6,
  color: "rgba(255,255,255,0.7)",
  fontSize: 10,
  cursor: "pointer",
}

// === NOS-198: deliberately skipped pages ===

const SKIPPED_BLOCK_STYLE: React.CSSProperties = {
  margin: "0 14px 10px",
  padding: "6px 10px",
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.07)",
  borderRadius: 8,
  fontSize: 10,
  color: "rgba(255,255,255,0.6)",
}

const SKIPPED_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "2px 0",
}

const SKIPPED_SLUG_STYLE: React.CSSProperties = {
  fontWeight: 600,
  color: "rgba(255,255,255,0.75)",
  textTransform: "capitalize" as const,
}

// === NOS-192: preview gate ===

const REVIEW_GATE_STYLE: React.CSSProperties = {
  margin: "10px 14px",
  padding: "10px 12px",
  background: "rgba(124,58,237,0.12)",
  border: "1px solid rgba(124,58,237,0.4)",
  borderRadius: 10,
  fontSize: 11,
  color: "rgba(255,255,255,0.8)",
}

const REVIEW_PRIMARY_BTN_STYLE: React.CSSProperties = {
  flex: 1,
  padding: "7px 10px",
  background: "rgba(124,58,237,0.55)",
  border: "1px solid rgba(124,58,237,0.7)",
  borderRadius: 8,
  color: "#fff",
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
}

const REVIEW_SECONDARY_BTN_STYLE: React.CSSProperties = {
  flex: 1,
  padding: "7px 10px",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 8,
  color: "rgba(255,255,255,0.85)",
  fontSize: 11,
  fontWeight: 500,
  cursor: "pointer",
}

// === NOS-190: import report ===

/** NOS-180: proposed-brand card (neutral panel chrome, distinct from warnings). */
const BRAND_CARD_STYLE: React.CSSProperties = {
  margin: "0 14px 10px",
  padding: "10px 12px",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 8,
  fontSize: 10,
  color: "rgba(255,255,255,0.75)",
}

const REPORT_CLEAN_STYLE: React.CSSProperties = {
  margin: "0 14px 10px",
  fontSize: 10,
  color: "rgba(255,255,255,0.5)",
}

const REPORT_STYLE: React.CSSProperties = {
  margin: "0 14px 10px",
  padding: "8px 10px",
  background: "rgba(245,158,11,0.08)",
  border: "1px solid rgba(245,158,11,0.25)",
  borderRadius: 8,
  fontSize: 10,
  color: "rgba(255,255,255,0.75)",
}

const REPORT_TITLE_STYLE: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 11,
  color: "#f59e0b",
  marginBottom: 4,
}

const REPORT_ROW_STYLE: React.CSSProperties = {
  padding: "1px 0",
}

/** NOS-229: missing homepage is the loudest report row — red, not amber. */
const REPORT_HOMEPAGE_MISSING_STYLE: React.CSSProperties = {
  padding: "2px 0",
  color: "#f87171",
  fontWeight: 600,
}

const REPORT_TOGGLE_STYLE: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "rgba(255,255,255,0.6)",
  cursor: "pointer",
  fontSize: 10,
  textDecoration: "underline",
  padding: "2px 0",
}

const REPORT_DETAILS_STYLE: React.CSSProperties = {
  marginTop: 4,
  paddingTop: 4,
  borderTop: "1px solid rgba(245,158,11,0.2)",
}

const REPORT_DETAIL_HEADING_STYLE: React.CSSProperties = {
  fontWeight: 600,
  marginTop: 4,
  color: "rgba(255,255,255,0.7)",
}

const REPORT_DETAIL_ROW_STYLE: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  padding: "1px 0",
  opacity: 0.85,
}

const JOB_PROGRESS_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  fontSize: 11,
  color: "rgba(255,255,255,0.85)",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
}

const MARK_ALL_BTN_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  margin: "0 14px 10px",
  padding: "8px 12px",
  background: "rgba(124,58,237,0.3)",
  border: "1px solid rgba(124,58,237,0.5)",
  borderRadius: 8,
  color: "#fff",
  fontSize: 11,
  fontWeight: 500,
  cursor: "pointer",
}

const LIST_STYLE: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "0 14px 14px",
}


const PAGE_LABEL_STYLE: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  color: "rgba(255,255,255,0.4)",
  marginBottom: 6,
}

const SECTION_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 0",
  fontSize: 11,
}

const SECTION_LABEL_STYLE: React.CSSProperties = {
  flex: 1,
  color: "rgba(255,255,255,0.8)",
}

const LINK_BTN_STYLE: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "rgba(255,255,255,0.6)",
  cursor: "pointer",
  fontSize: 11,
  textDecoration: "underline",
}

// NOS-183: per-page side-by-side compare launcher
const COMPARE_BTN_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  background: "rgba(96,165,250,0.15)",
  border: "none",
  borderRadius: 6,
  color: "#60a5fa",
  cursor: "pointer",
}

const CHECK_BTN_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  background: "rgba(34,197,94,0.3)",
  border: "none",
  borderRadius: 6,
  color: "#22c55e",
  cursor: "pointer",
}
